import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, PageHero, CtaSection } from "../components/common";
import { IMG, STATS } from "../data/site";

const sections = [
  { label: "Who We Are", title: "An international engineering company rooted in European precision", body: "STERILTRON is a Hungary-based engineering and manufacturing company specialising in sterilization, medical waste treatment and industrial process technologies. From our Hungarian headquarters we serve healthcare, industrial and public-sector clients across the world, combining decades of applied engineering with a culture of meticulous craftsmanship." },
  { label: "Engineering Philosophy", title: "We design for the whole lifecycle, not just the sale", body: "Our engineers own each system from concept to commissioning and beyond. We favour proven materials, generous safety margins and serviceable architecture — because equipment that treats hazardous waste must remain dependable for decades, not seasons." },
  { label: "Our Technology", title: "Validated processes, transparent control", body: "Every STERILTRON system is built around validated thermal processes and programmable control that documents each cycle. This gives operators complete assurance and gives facilities the audit trail regulators expect." },
  { label: "Quality & Reliability", title: "Built to international standards", body: "We manufacture to recognised European standards for pressure equipment and medical devices, using AISI 316L stainless steel and rigorous factory acceptance testing. Reliability is engineered in, not inspected on." },
  { label: "Environmental Responsibility", title: "Cleaner outcomes by design", body: "On-site treatment reduces the transport of hazardous waste, while energy and water recovery lower the footprint of every cycle. Environmental responsibility is a design constraint we embrace, not an afterthought." },
  { label: "International Operations", title: "A global footprint, a local partner", body: "With projects across multiple continents, we support customers through the full lifecycle — engineering, delivery, installation, validation and long-term service — adapting to local requirements wherever we operate." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us | STERILTRON — Engineering Solutions Beyond Expectations"
        description="STERILTRON is a Hungary-based engineering company delivering advanced sterilization and medical waste treatment technologies for international operations."
        path="/about"
      />
      <PageHero
        label="About STERILTRON"
        titleLines={["Engineering Solutions", "Beyond Expectations"]}
        description="Headquartered in Rétság, Hungary, STERILTRON engineers and manufactures the systems that keep healthcare and industry clean, safe and compliant — worldwide."
      />

      {/* IMAGE + STATS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="overflow-hidden border border-slate-200">
            <img src={IMG.aboutFacility} alt="STERILTRON headquarters and manufacturing facility in Rétság" className="w-full h-[360px] md:h-[560px] object-cover" />
          </Reveal>
          <div className="mt-px grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border-x border-b border-slate-200">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white p-8">
                <div className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-brand-900">{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {sections.map((s, i) => (
            <div key={s.label} className="grid lg:grid-cols-12 gap-8 lg:gap-16 py-14 md:py-20 border-t border-slate-200">
              <div className="lg:col-span-4">
                <SectionLabel>{s.label}</SectionLabel>
                <span className="mt-6 block font-mono text-sm text-slate-400">{String(i + 1).padStart(2, "0")} / 06</span>
              </div>
              <div className="lg:col-span-8">
                <h2 className="font-heading text-2xl md:text-4xl font-bold tracking-tighter text-brand-950 text-balance">{s.title}</h2>
                <p className="mt-6 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          <div className="bg-brand-900 text-white p-10 md:p-16">
            <SectionLabel dark>Mission</SectionLabel>
            <p className="mt-6 font-heading text-2xl md:text-3xl font-medium tracking-tight leading-snug text-balance">
              To engineer dependable technologies that make the treatment of medical and industrial waste safer, cleaner and more efficient everywhere we operate.
            </p>
          </div>
          <div className="bg-brand-800 text-white p-10 md:p-16">
            <SectionLabel dark>Vision</SectionLabel>
            <p className="mt-6 font-heading text-2xl md:text-3xl font-medium tracking-tight leading-snug text-balance">
              A future in which every facility can manage its hazardous waste responsibly, on-site, with confidence — contributing to a cleaner world.
            </p>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
